import { useCallback, useMemo, useState } from "react";

export interface AttachmentItem {
  // Stable local id for React keys and removal, independent of any server id.
  key: string;
  filename: string;
  // The raw file: rendered as a chip now, uploaded to the conversation at send.
  file: File;
}

interface UseAttachmentTrayResult {
  items: AttachmentItem[];
  addFiles: (files: File[]) => void;
  removeItem: (key: string) => void;
  clear: () => void;
  files: File[];
}

// Holds the files a user has attached to the composer, in the order added.
//
// Files are kept raw and only uploaded when the message is sent (see `Composer`
// / `NewSessionPicker`). Because nothing hits the server until send, removing a
// chip or navigating away just drops local state — there are no pre-send
// artifacts to clean up.
export function useAttachmentTray(): UseAttachmentTrayResult {
  const [items, setItems] = useState<AttachmentItem[]>([]);

  const addFiles = useCallback((files: File[]) => {
    setItems((prev) => [
      ...prev,
      ...files.map((file) => ({ key: crypto.randomUUID(), filename: file.name, file })),
    ]);
  }, []);

  const removeItem = useCallback(
    (key: string) => setItems((prev) => prev.filter((item) => item.key !== key)),
    []
  );

  const clear = useCallback(() => setItems([]), []);

  const files = useMemo(() => items.map((item) => item.file), [items]);

  return { items, addFiles, removeItem, clear, files };
}
